import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.trim.js";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { userLocalTimezone } from 'utils/timezone';
import filterObject from 'utils/filterObject';
import getLocalisedTime from 'utils/times/getLocalisedTime';
import getLocalisedDate from 'utils/dates/getLocalisedDate';
import Button from 'components/Button';
import Card from 'components/Card';
import CheckboxOption from 'components/CheckboxOption';
import FormField from 'components/FormField';
import FormStack from 'components/FormStack';
import Hint from 'components/Hint';
import Modal from 'components/Modal';
import ModalHeadline from 'components/ModalHeadline';
import Panel from 'components/Panel';
import TextArea from 'components/TextArea';
import TextInput from 'components/TextInput';
import useForm from 'hooks/useForm';
import ConfirmationModal from './ConfirmationModal';
import UnlockingOptionsPanel from './UnlockingOptionsPanel';
import { formatSectionLockingFields } from './formatSectionLockingFields';
import { checkIsPastSectionUnlockDateTime, getDateAndTimeFromDateTime } from './utils';
import StickyBox from 'components/StickyBox';
var defaultSectionData = {
  name: '',
  lockingMode: null
};
var dateOptions = {
  month: 'short',
  year: 'numeric',
  day: 'numeric'
};
function SectionEditorModalUi(_ref) {
  var _ref$initialSectionDa = _ref.initialSectionData,
    initialSectionData = _ref$initialSectionDa === void 0 ? defaultSectionData : _ref$initialSectionDa,
    isSaving = _ref.isSaving,
    onSubmit = _ref.onSubmit,
    onClose = _ref.onClose,
    _ref$isSubsection = _ref.isSubsection,
    isSubsection = _ref$isSubsection === void 0 ? false : _ref$isSubsection,
    _ref$inContentManager = _ref.inContentManagerContext,
    inContentManagerContext = _ref$inContentManager === void 0 ? false : _ref$inContentManager;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _getDateAndTimeFromDa = getDateAndTimeFromDateTime(initialSectionData.unlockDateTime),
    date = _getDateAndTimeFromDa.date,
    time = _getDateAndTimeFromDa.time;
  var isPastInitialUnlockTime = checkIsPastSectionUnlockDateTime(initialSectionData.lockingMode, date, time, initialSectionData.unlockTimezone);
  var _useForm = useForm({
      fields: {
        name: {
          getDefaultValue: function getDefaultValue() {
            return initialSectionData.name;
          },
          required: true
        },
        description: {
          getDefaultValue: function getDefaultValue() {
            return initialSectionData.description || '';
          }
        },
        isLocked: {
          getDefaultValue: function getDefaultValue() {
            if (isPastInitialUnlockTime) {
              return false;
            }
            return Boolean(initialSectionData.lockingMode);
          }
        },
        lockingMode: {
          getDefaultValue: function getDefaultValue() {
            return initialSectionData.lockingMode || null;
          }
        },
        unlockDate: {
          getDefaultValue: function getDefaultValue() {
            if (initialSectionData.unlockDateTime) {
              return date;
            }
            return null;
          }
        },
        unlockTime: {
          getDefaultValue: function getDefaultValue() {
            if (initialSectionData.unlockDateTime) {
              return time ? time : '08:00:00';
            }
            return '08:00:00';
          }
        },
        unlockTimezone: {
          getDefaultValue: function getDefaultValue() {
            return initialSectionData.unlockTimezone || userLocalTimezone;
          }
        }
      }
    }),
    fields = _useForm.fields,
    wasChanged = _useForm.wasChanged,
    hasErrors = _useForm.hasErrors;

  // If the section is unlocked due to it being time locked and the locking date having passed,
  // we do not count the checking of the "Lock section" checkbox as a form value change
  // as the section already has a locked mode set (even if the UI shows it as unlocked).
  var wasFormValueChanged = isPastInitialUnlockTime && fields.lockingMode.defaultValue === 'time' ? [fields.name, fields.description, fields.lockingMode, fields.unlockDate, fields.unlockTime, fields.unlockTimezone].some(function (field) {
    return field.wasChanged;
  }) : wasChanged;
  var emptyNameAlert = fields.name.wasChanged ? fields.name.error : undefined;
  function handleSubmit(_x) {
    return _handleSubmit.apply(this, arguments);
  }
  function _handleSubmit() {
    _handleSubmit = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(e) {
      var _fields$name$changedV, _fields$description$c, _formatSectionLocking, lockingMode, unlockDateTime, unlockTimezone;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            e.preventDefault();
            if (!wasFormValueChanged) {
              _context.next = 5;
              break;
            }
            _formatSectionLocking = formatSectionLockingFields(fields), lockingMode = _formatSectionLocking.lockingMode, unlockDateTime = _formatSectionLocking.unlockDateTime, unlockTimezone = _formatSectionLocking.unlockTimezone;
            _context.next = 5;
            return onSubmit(filterObject({
              name: (_fields$name$changedV = fields.name.changedValue) === null || _fields$name$changedV === void 0 ? void 0 : _fields$name$changedV.trim(),
              description: (_fields$description$c = fields.description.changedValue) === null || _fields$description$c === void 0 ? void 0 : _fields$description$c.trim(),
              lockingMode: lockingMode,
              unlockDateTime: unlockDateTime,
              unlockTimezone: unlockTimezone
            }));
          case 5:
            onClose();
          case 6:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return _handleSubmit.apply(this, arguments);
  }
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isConfirmationModalOpen = _useState2[0],
    setConfirmationModalOpen = _useState2[1];
  function handleClose() {
    if (wasFormValueChanged) {
      setConfirmationModalOpen(true);
    } else {
      onClose();
    }
  }
  var editMode = Boolean(initialSectionData.name);
  var sectionTitle = editMode ? t('actions_edit_section') : t('actions_add_section');
  var subsectionTitle = editMode ? t('actions_edit_subsection') : t('actions_add_subsection');
  var modalTitle = isSubsection ? subsectionTitle : sectionTitle;
  var submitLabel = inContentManagerContext ? t('actions_done') : t('actions_save_and_close');
  var titleId = 'section-editor-title';
  var isPastSetUnlockTime = checkIsPastSectionUnlockDateTime(initialSectionData.lockingMode, fields.unlockDate.value, fields.unlockTime.value, fields.unlockTimezone.value);
  return /*#__PURE__*/React.createElement(Modal, {
    width: "wide",
    "aria-labelledby": titleId,
    onRequestClose: handleClose
  }, /*#__PURE__*/React.createElement(Card, {
    variant: "raised",
    as: "form",
    overflow: "visible",
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(ModalHeadline, {
    title: modalTitle,
    titleId: titleId,
    onClose: handleClose
  }), /*#__PURE__*/React.createElement(Panel, null, /*#__PURE__*/React.createElement(FormStack, null, /*#__PURE__*/React.createElement(FormField, {
    required: true,
    label: "".concat(t('form_title'), " *"),
    alert: emptyNameAlert
  }, /*#__PURE__*/React.createElement(TextInput, {
    "data-autofocus": true,
    type: "text",
    value: fields.name.value,
    onChange: function onChange(e) {
      return fields.name.set(e.target.value);
    }
  })), /*#__PURE__*/React.createElement(FormField, {
    label: t('form_description')
  }, /*#__PURE__*/React.createElement(TextArea, {
    maxRows: 8,
    minRows: 3,
    value: fields.description.value,
    onChange: function onChange(e) {
      return fields.description.set(e.target.value);
    }
  })), inContentManagerContext && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CheckboxOption, {
    bold: true,
    label: t('actions_lock_section'),
    description: t('lock_section_info'),
    descriptionType: "subtitle",
    checked: fields.isLocked.value,
    onChange: function onChange() {
      return fields.isLocked.set(function (prev) {
        return !prev;
      });
    }
  }), isPastSetUnlockTime && /*#__PURE__*/React.createElement(Hint, {
    iconOffset: -2,
    ml: "xl",
    mt: "xs"
  }, t('section_was_unlocked_info', {
    date: getLocalisedDate({
      date: fields.unlockDate.value,
      isInLocalTimezone: true,
      options: dateOptions
    }),
    time: fields.unlockTime.value ? getLocalisedTime(fields.unlockTime.value) : null
  })), fields.isLocked.value && /*#__PURE__*/React.createElement(UnlockingOptionsPanel, {
    fields: fields
  })))), /*#__PURE__*/React.createElement(StickyBox, {
    placement: "bottom",
    borderBottomRadius: "inherit",
    textAlign: "center",
    p: "m",
    mt: "-m"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "primary",
    isLoading: isSaving,
    isDisabled: !wasFormValueChanged || hasErrors
  }, submitLabel))), isConfirmationModalOpen && /*#__PURE__*/React.createElement(ConfirmationModal, {
    onConfirm: onClose,
    onCancel: function onCancel() {
      return setConfirmationModalOpen(false);
    },
    editMode: editMode
  }));
}
export default SectionEditorModalUi;